/**
 * Form Validation Utilities
 */

/**
 * Validate email format
 * @param {string} email - Email address to validate
 * @returns {object} - {isValid: boolean, error: string}
 */
export const validateEmail = (email) => {
  if (!email || email.trim() === '') {
    return { isValid: false, error: 'Email is required' };
  }
  
  // RFC 5322 compliant email regex (simplified)
  const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
  
  if (!emailRegex.test(email)) {
    return { isValid: false, error: 'Please enter a valid email address (e.g., user@example.com)' };
  }
  
  return { isValid: true, error: '' };
};

/**
 * Validate phone number format
 * Must be exactly 10 digits (numeric only)
 * @param {string} phoneNumber - Phone number to validate
 * @returns {object} - {isValid: boolean, error: string}
 */
export const validatePhoneNumber = (phoneNumber) => {
  if (!phoneNumber || phoneNumber.trim() === '') {
    return { isValid: false, error: 'Phone number is required' };
  }
  
  // Remove all non-digit characters for validation
  const digitsOnly = phoneNumber.replace(/\D/g, '');
  
  // Check if contains any alphabets
  if (/[a-zA-Z]/.test(phoneNumber)) {
    return { isValid: false, error: 'Phone number cannot contain letters' };
  }
  
  // Must be exactly 10 digits
  if (digitsOnly.length < 10) {
    return { isValid: false, error: 'Phone number must be exactly 10 digits' };
  }
  
  if (digitsOnly.length > 10) {
    return { isValid: false, error: 'Phone number cannot exceed 10 digits' };
  }
  
  return { isValid: true, error: '' };
};

/**
 * Format phone number as user types
 * Removes non-numeric characters and limits to 10 digits
 * @param {string} value - Input value
 * @returns {string} - Formatted phone number
 */
export const formatPhoneNumber = (value) => {
  // Remove all non-digit characters
  const digitsOnly = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  return digitsOnly.slice(0, 10);
};

/**
 * Validate required field
 * @param {string} value - Field value
 * @param {string} fieldName - Name of the field for error message
 * @returns {object} - {isValid: boolean, error: string}
 */
export const validateRequired = (value, fieldName = 'This field') => {
  if (!value || value.trim() === '') {
    return { isValid: false, error: `${fieldName} is required` };
  }
  return { isValid: true, error: '' };
};
